/**
 * Numbering Service
 * Handles generation of project and quotation numbers with customizable formats
 */

import { supabase } from '../supabaseClient';
import {
  NumberingConfig,
  DEFAULT_NUMBERING_CONFIG,
} from '../types/numbering.types';
import { logger } from '../lib/logger';
import { saveSetting } from './settingsService';

/**
 * Get numbering configuration for a team
 * Loads team-wide setting (any team member's config)
 */
export async function getNumberingConfig(
  teamId: string
): Promise<NumberingConfig> {
  try {
    const { data, error } = await supabase
      .from('user_settings')
      .select('setting_value')
      .eq('team_id', teamId)
      .eq('setting_key', 'numbering_config')
      .limit(1)
      .maybeSingle();

    if (error) {
      logger.debug('Error fetching numbering config, using defaults:', error);
      return DEFAULT_NUMBERING_CONFIG;
    }

    if (!data) {
      logger.debug('No numbering config found, using defaults');
      return DEFAULT_NUMBERING_CONFIG;
    }

    return { ...DEFAULT_NUMBERING_CONFIG, ...data.setting_value };
  } catch (error) {
    logger.error('Error fetching numbering config:', error);
    return DEFAULT_NUMBERING_CONFIG;
  }
}

/**
 * Save numbering configuration for a team
 */
export async function saveNumberingConfig(
  __userId: string,
  teamId: string,
  config: NumberingConfig
): Promise<void> {
  // Goes through settingsService rather than writing user_settings directly.
  //
  // This used to upsert with onConflict: 'user_id,setting_key' — a conflict
  // target that ignores team_id. With the stale global unique constraint still
  // in place, that silently RE-PARENTED an existing row: saving a numbering
  // config for team B moved team A's row to team B. See #115.
  //
  // userId is retained in the signature for callers and is no longer used:
  // saveSetting resolves the writer from the session, so a caller can no longer
  // name someone else as the writer.
  //
  // It does NOT mean user_id tracks the last writer. saveSetting's UPDATE names
  // only setting_value and updated_at, so on an existing row user_id stays the
  // FIRST writer forever. That is worth knowing because the surviving
  // user_id-only RLS policies on this table treat it as identity rather than
  // provenance — see #113.
  const result = await saveSetting('numbering_config', config, teamId);

  if (!result.success) {
    logger.error('Error saving numbering config:', result.error);
    throw new Error(result.error ?? 'Failed to save numbering config');
  }

  logger.info('Numbering config saved successfully');
}

/**
 * Generate next project number using database function
 * Falls back to client-side generation if database function doesn't exist
 */
export async function generateProjectNumber(teamId: string): Promise<string> {
  try {
    // Get configuration
    const config = await getNumberingConfig(teamId);

    // Try to call database function first
    const { data, error } = await supabase.rpc('generate_project_number', {
      p_team_id: teamId,
      p_prefix: config.projectPrefix,
      p_padding: config.padding,
      p_separator: config.separator,
    });

    // If function exists, use it
    if (!error && data) {
      logger.info(`Generated project number: ${data}`);
      return data;
    }

    // Fallback: Generate client-side if database function doesn't exist
    logger.warn(
      'Database function not found, using fallback number generation'
    );

    // Get count of existing projects for this team
    const { count, error: countError } = await supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('team_id', teamId);

    if (countError) {
      logger.error('Error counting projects:', countError);
      // Use timestamp as fallback
      const timestamp = Date.now();
      return `${config.projectPrefix}${config.separator}${timestamp}`;
    }

    // Generate number based on count
    const nextNumber = (count || 0) + 1;
    const paddedNumber = nextNumber.toString().padStart(config.padding, '0');
    const projectNumber = `${config.projectPrefix}${config.separator}${paddedNumber}`;

    logger.info(`Generated fallback project number: ${projectNumber}`);
    return projectNumber;
  } catch (error) {
    logger.error('Error generating project number:', error);
    throw new Error('Failed to generate project number');
  }
}

/**
 * The next quotation number for a project.
 *
 * Delegates to the `generate_quotation_number` RPC and to nothing else.
 *
 * #136: this function used to carry a client-side fallback that counted the
 * project's quotations and added one, used whenever the RPC was missing or
 * errored. The RPC counted too, so both were wrong in the same way -- delete a
 * middle quotation and the count stops matching the highest number in use.
 *
 * The fallback is deleted rather than fixed. A second copy of the rule is what
 * let the first one stay wrong: generateNextQuotationNumberForProject() below
 * has held a third, correct copy since #63, used only by copy/version, and
 * nothing ever compared the two. The database is the single source of truth for
 * this number, and migration 20260908141500 makes it derive from the maximum in
 * use rather than from a count.
 *
 * Throws rather than inventing a number. Three of the four callers -- Dashboard,
 * QuotationDataGrid and AppRoutes -- catch and fall back to a `Q-<epoch>`
 * placeholder, so a failure shows up as a visibly odd number on the created row
 * instead of a silent collision on the next one. The fourth,
 * `useNumbering.getNextQuotationNumber`, returns undefined with no placeholder.
 * Nothing consumes it today so nothing breaks, but it is not a fourth safe
 * caller and must not be read as one.
 *
 * (This said "both call sites" when there were four; it was counting the two
 * creation paths #136 is about. Corrected in review before shipping. The
 * `Q-<epoch>` placeholder is also why the RPC bounds its tail to nine digits --
 * see the migration.)
 */
export async function generateQuotationNumber(
  teamId: string,
  projectNumber: string
): Promise<string> {
  const config = await getNumberingConfig(teamId);

  const { data, error } = await supabase.rpc('generate_quotation_number', {
    p_team_id: teamId,
    p_project_number: projectNumber,
    p_quotation_prefix: config.quotationPrefix,
    p_padding: config.padding,
    p_separator: config.separator,
  });

  if (error || !data) {
    logger.error('Could not generate quotation number:', error);
    throw new Error('Failed to generate quotation number');
  }

  logger.info(`Generated quotation number: ${data}`);
  return data;
}

/**
 * Next quotation number for a project — "project number, then running number".
 *
 * #63: copies and new versions used to be named
 * `PRJ-0008-QT-0001-COPY-1786567669644` and `…-V2`, neither of which is the
 * scheme this system uses. Both now take the next running number inside their
 * project, like any other quotation: PRJ-0008-QT-0002.
 *
 * Derived from the highest number in use rather than from a count, unlike the
 * `generate_quotation_number` RPC. A count repeats itself once anything has
 * been deleted — with QT-0001 and QT-0003 present, a count of 2 proposes
 * QT-0003, which already exists. Changing the RPC needs a migration, so the
 * safe rule lives here and the result is checked free before being returned.
 *
 * @param teamId Active team.
 * @param projectId Project the quotation belongs to. When absent there is no
 *   project sequence to draw on, so the source number becomes the stem.
 * @param sourceQuotationNumber The number being copied, used only as that stem.
 * @param version Version the new row will carry — the uniqueness key is
 *   (team_id, quotation_number, version), so it participates in the check.
 */
export async function generateNextQuotationNumberForProject(
  teamId: string,
  projectId: string | undefined,
  sourceQuotationNumber: string,
  version = 1
): Promise<string> {
  const config = await getNumberingConfig(teamId);
  const prefix = await resolveQuotationPrefix(
    teamId,
    projectId,
    sourceQuotationNumber,
    config
  );

  const { data: existing, error } = await supabase
    .from('quotations')
    .select('quotation_number, version')
    .eq('team_id', teamId)
    .like('quotation_number', `${prefix}%`);

  if (error) {
    logger.error('Could not read existing quotation numbers:', error);
    throw new Error('Failed to generate quotation number');
  }

  const rows = existing || [];
  const trailing = new RegExp(`^${escapeForRegExp(prefix)}(\\d+)$`);
  let highest = 0;
  for (const row of rows) {
    const match = trailing.exec(row.quotation_number || '');
    if (match) highest = Math.max(highest, parseInt(match[1], 10));
  }

  // quotations_team_unique covers (team_id, quotation_number, version), so step
  // past any pair already in use rather than letting the constraint reject it.
  const taken = new Set(
    rows.map(row => `${row.quotation_number}::${row.version ?? 1}`)
  );

  let candidate: string;
  let next = highest + 1;
  do {
    candidate = `${prefix}${next.toString().padStart(config.padding, '0')}`;
    next++;
  } while (taken.has(`${candidate}::${version}`));

  logger.info(`Generated quotation number: ${candidate}`);
  return candidate;
}

/**
 * The stem a project's quotation numbers share, e.g. "PRJ-0008-QT-".
 */
async function resolveQuotationPrefix(
  teamId: string,
  projectId: string | undefined,
  sourceQuotationNumber: string,
  config: NumberingConfig
): Promise<string> {
  if (projectId) {
    const { data: project } = await supabase
      .from('projects')
      .select('project_number')
      .eq('id', projectId)
      .eq('team_id', teamId)
      .maybeSingle();

    if (project?.project_number) {
      return `${project.project_number}${config.separator}${config.quotationPrefix}${config.separator}`;
    }
  }

  // No project sequence to draw on. Continue the source's own number instead:
  // PRJ-0008-QT-0001 yields the stem PRJ-0008-QT-, so an unlinked quotation
  // still reads like every other one.
  const withoutCounter = sourceQuotationNumber.replace(/\d+$/, '');
  return withoutCounter || `${sourceQuotationNumber}${config.separator}`;
}

function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Validate numbering configuration
 */
export function validateNumberingConfig(config: NumberingConfig): string[] {
  const errors: string[] = [];

  if (!config.projectPrefix || config.projectPrefix.length === 0) {
    errors.push('Project prefix cannot be empty');
  }

  if (!config.quotationPrefix || config.quotationPrefix.length === 0) {
    errors.push('Quotation prefix cannot be empty');
  }

  if (config.padding < 1 || config.padding > 10) {
    errors.push('Padding must be between 1 and 10 digits');
  }

  // Separator can be empty (for formats like "PRJ0001" instead of "PRJ-0001")
  // No validation needed for separator

  // Check for invalid characters
  const validPattern = /^[A-Za-z0-9]+$/;
  if (!validPattern.test(config.projectPrefix)) {
    errors.push('Project prefix can only contain letters and numbers');
  }

  if (!validPattern.test(config.quotationPrefix)) {
    errors.push('Quotation prefix can only contain letters and numbers');
  }

  return errors;
}

/**
 * Preview what numbers would look like with given configuration
 */
export function previewNumbers(config: NumberingConfig): {
  projectExample: string;
  quotationExample: string;
} {
  const exampleProjectNum = '1'.padStart(config.padding, '0');
  const exampleQuotationNum = '1'.padStart(config.padding, '0');

  const projectExample = `${config.projectPrefix}${config.separator}${exampleProjectNum}`;
  const quotationExample = `${projectExample}${config.separator}${config.quotationPrefix}${config.separator}${exampleQuotationNum}`;

  return {
    projectExample,
    quotationExample,
  };
}
